import { useState, useEffect, useRef } from "react";
import { useI18n } from "@/i18n";

type Scene = 0 | 1 | 2 | 3;

const SCENE_DURATION: Record<Scene, number> = {
  0: 100,
  1: 1200,
  2: 1400,
  3: 1400,
};

// Icons
const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TagIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);

const ShareIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const UnlockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </svg>
);

type TabType = "flow" | "steps";

export function HowItWorksSection() {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<TabType>("steps");

  return (
    <section id="how-it-works" className="hiw-section">
      <div className="hiw-inner">
        {/* Header */}
        <header className="hiw-header">
          <h2>{t.home.howItWorks}</h2>
          <p>{t.home.hiwSubtitle}</p>
        </header>

        {/* Tab Switcher */}
        <div className="hiw-tabs">
          <button
            type="button"
            className={`hiw-tab ${activeTab === "steps" ? "active" : ""}`}
            onClick={() => setActiveTab("steps")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            {t.home.hiwTabSteps}
          </button>
          <button
            type="button"
            className={`hiw-tab ${activeTab === "flow" ? "active" : ""}`}
            onClick={() => setActiveTab("flow")}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12h8M12 8v8" />
            </svg>
            {t.home.hiwTabFlow}
          </button>
        </div>

        {/* Tab Content */}
        <div className="hiw-content">
          {activeTab === "steps" ? (
            <StepsContent />
          ) : (
            <PaymentFlowContent />
          )}
        </div>
      </div>
    </section>
  );
}

// Payment Flow Animation Content
function PaymentFlowContent() {
  const { t } = useI18n();

  const captions: Record<Scene, { main: string; sub: string }> = {
    0: { main: "", sub: "" },
    1: { main: t.home.pfCaption1Main, sub: t.home.pfCaption1Sub },
    2: { main: t.home.pfCaption2Main, sub: t.home.pfCaption2Sub },
    3: { main: t.home.pfCaption3Main, sub: t.home.pfCaption3Sub },
  };

  const [scene, setScene] = useState<Scene>(0);
  const [isRunning, setIsRunning] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isRunning) return;
    const timer = setTimeout(() => {
      setScene((prev) => ((prev < 3 ? prev + 1 : 1) as Scene));
    }, SCENE_DURATION[scene]);
    return () => clearTimeout(timer);
  }, [scene, isRunning]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isRunning) {
          setIsRunning(true);
          setScene(1);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [isRunning]);

  return (
    <div className="hiw-flow" ref={containerRef}>
      {/* Diagram */}
      <div className="pf-diagram">
        {/* Buyer */}
        <div className={`pf-card ${scene >= 1 ? "show" : ""} ${scene >= 3 ? "done" : ""}`}>
          <div className="pf-card-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
            </svg>
          </div>
          <span className="pf-card-name">{t.home.pfBuyer}</span>
          {scene >= 3 && <span className="pf-tick">✓</span>}
        </div>

        {/* Arrow 1 */}
        <div className={`pf-arrow ${scene >= 2 ? "on" : ""}`}>
          <div className="pf-arrow-track"><div className="pf-arrow-bar" /></div>
          {scene === 2 && <div className="pf-arrow-dot"><span>$</span></div>}
        </div>

        {/* Smart Contract */}
        <div className={`pf-card pf-card--contract ${scene >= 1 ? "show" : ""} ${scene === 2 ? "glow" : ""}`}>
          <div className="pf-card-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <path d="M7 8h10M7 12h7M7 16h4" />
            </svg>
          </div>
          <span className="pf-card-name">{t.home.pfSmartContract}</span>
          <span className="pf-card-badge">{t.home.pfOnChain}</span>
        </div>

        {/* Arrow 2 */}
        <div className={`pf-arrow pf-arrow--green ${scene >= 3 ? "on" : ""}`}>
          <div className="pf-arrow-track"><div className="pf-arrow-bar" /></div>
          {scene === 3 && <div className="pf-arrow-dot pf-arrow-dot--green"><span>$</span></div>}
        </div>

        {/* Seller */}
        <div className={`pf-card pf-card--seller ${scene >= 1 ? "show" : ""} ${scene === 3 ? "pulse" : ""} ${scene >= 3 ? "done" : ""}`}>
          <div className="pf-card-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
            </svg>
          </div>
          <span className="pf-card-name">{t.home.pfSeller}</span>
          {scene >= 3 && <span className="pf-tick">✓</span>}
        </div>
      </div>

      {/* Caption */}
      <div className="pf-caption">
        <p className={`pf-caption-title ${scene >= 1 ? "show" : ""}`} key={`t${scene}`}>
          {captions[scene].main}
        </p>
        <p className={`pf-caption-desc ${scene >= 1 ? "show" : ""}`} key={`d${scene}`}>
          {captions[scene].sub}
        </p>
      </div>

      {/* Progress */}
      <div className="pf-steps">
        {[1, 2, 3].map((n) => (
          <button
            key={n}
            type="button"
            className={`pf-step ${scene === n ? "active" : ""} ${scene > n ? "done" : ""}`}
            onClick={() => { setScene(n as Scene); setIsRunning(true); }}
            aria-label={`Step ${n}`}
          />
        ))}
      </div>

      {/* Features */}
      <div className="pf-features">
        <div className="pf-feat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span>{t.home.pfFeatNonCustodial}</span>
        </div>
        <div className="pf-feat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>{t.home.pfFeatInstant}</span>
        </div>
        <div className="pf-feat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span>{t.home.pfFeatTransparent}</span>
        </div>
      </div>
    </div>
  );
}

// Steps Content
function StepsContent() {
  const { t } = useI18n();

  const steps = [
    { icon: <UploadIcon />, title: t.home.step1Title, desc: t.home.step1Desc },
    { icon: <TagIcon />, title: t.home.step2Title, desc: t.home.step2Desc },
    { icon: <ShareIcon />, title: t.home.step3Title, desc: t.home.step3Desc },
    { icon: <UnlockIcon />, title: t.home.step4Title, desc: t.home.step4Desc },
  ];

  return (
    <div className="hiw-steps-content">
      <div className="hiw-steps-grid">
        {steps.map((step, i) => (
          <div key={i} className="hiw-step-card">
            <div className="hiw-step-icon">{step.icon}</div>
            <h3 className="hiw-step-title">{step.title}</h3>
            <p className="hiw-step-desc">{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
